import { NgTemplateOutlet } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, output } from '@angular/core'
import { RouterLink } from '@angular/router'
import { assert } from '@ftr/contracts/shared/Assertions'
import { Color } from '../design-system'
import { Icon, IconComponent } from '../icon'
import { LoaderComponent, LoaderSize } from '../loader'

interface MenuItemBase {
  label: string
  secondaryText?: string
  icon?: Icon
  iconColor?: Color
  labelColor?: Color
  openInNewWindow?: boolean
  hideOpenInNewWindowIcon?: boolean
  isLoading?: boolean
  testId?: string
}

export interface MenuItemLink extends MenuItemBase {
  path: string[]
  queryParams?: Record<string, string>
}

export interface MenuItemButton extends MenuItemBase {
  action: () => void
  isDestructive?: boolean
  isSelected?: boolean
}

export type MenuItem = MenuItemBase | MenuItemLink | MenuItemButton

@Component({
  selector: 'ftr-menu-item',
  templateUrl: './menu-item.component.html',
  styleUrls: ['./menu-item.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, RouterLink, NgTemplateOutlet, LoaderComponent],
})
export class MenuItemComponent {
  #destroyRef = inject(DestroyRef)

  readonly menuItem = input.required<MenuItem>()
  readonly trackClicks = input<boolean>(false)

  readonly itemClicked = output()

  readonly defaultIconColor = computed<Color>(() => {
    const item = this.menuItem()
    return this.isButton(item) && item.isDestructive ? Color.Error50 : Color.Grey40
  })

  readonly shouldShowOpenInNewWindowIcon = computed<boolean>(() => {
    const item = this.menuItem()
    return item.openInNewWindow === true && item.hideOpenInNewWindowIcon !== true
  })

  readonly isLoading = computed<boolean>(() => {
    return this.menuItem().isLoading === true
  })

  protected handleButtonClicked(): void {
    const item = this.menuItem()

    assert(this.isButton(item), 'handleButtonClicked called on non-button menu item')
    item.action()

    // Some usages of menus may remove the menu item from the DOM immediately after the action is called
    // or due to document click events. Skip emitting if this component has been destroyed to avoid angular errors.
    if (!this.#destroyRef.destroyed) {
      this.itemClicked.emit()
    }
  }

  protected isLink(menuItem: MenuItem): menuItem is MenuItemLink {
    return 'path' in menuItem
  }

  protected isButton(menuItem: MenuItem): menuItem is MenuItemButton {
    return 'action' in menuItem
  }

  protected isSelectableButton(menuItem: MenuItem): menuItem is MenuItemButton {
    return 'isSelected' in menuItem
  }

  protected readonly COLOR = Color
  protected readonly LOADER_SIZE = LoaderSize
}
